import { Link, registerComponent, useMatchMobile, withProgressiveHydration } from '@avensia/excite';
import { Document, type StyleDecorators } from '@avensia/excite-contentful';
import { styled } from '@glitz/react';
import { decorators } from 'webapp/html-decorators/fontSizeDecorator';
import * as React from 'react';
import { useMemo } from 'react';
import Image from 'webapp/components/image';
import { Block, contentWidthDecorator, desktopEdgeMarginDecorator } from 'webapp/components/page-layout';
import { useNinetailedClickEvent } from '@avensia/excite-ninetailed';
import { useViewEventTrigger } from 'webapp/tracking';
import { addViewPromotionToDataLayer, addSelectPromotionToDataLayer } from 'webapp/tracking/events/promotions';
import { createMaxSmallViewportDecorator, createMinLargeViewportDecorator } from 'webapp/viewport';
import Countdown from 'webapp/shared/Countdown';
import { isSvenssons } from 'webapp/theme';
import ButtonBlock from './ButtonBlock';
import { ButtonBlockGroup } from 'webapp/components/ButtonBlockGroup';
import { linkWithoutQueryParams } from 'webapp/link-url-decorator';
import { useTrackingBlock } from '@avensia/excite-ninetailed';

export default registerComponent<NordicNest.Api.NNHeroBlock>(React.memo(withProgressiveHydration(HeroBlock)));

function HeroBlock(props: NordicNest.Api.NNHeroBlock) {
  const isMobile = useMatchMobile();
  const image = isMobile ? props.smallViewportImage || props.largeViewportImage : props.largeViewportImage;

  const self = React.createRef<HTMLDivElement>();
  useViewEventTrigger(self, addViewPromotionToDataLayer, {
    content: props,
  });

  useTrackingBlock(self);
  const ninetailedClickEvent = useNinetailedClickEvent();

  const contentDecorator = React.useMemo(
    () =>
      styled(
        {
          backgroundImage: `linear-gradient(0deg, transparent 0%, rgba(0,0,0,${props.gradientOpacity}) 50%, transparent 100%)`,
        },
        props.contentWidth === 'normal' && placementDecorator,
      ),
    [props.contentWidth, props.gradientOpacity],
  );

  const args: { [placeholder: string]: React.ReactNode } = useMemo(
    () => ({
      ...(props.countdown ? { countdown: <Countdown countdown={props.countdown} /> } : {}),
      ...(props.buttons ? { buttons: <ButtonBlockGroup buttons={props.buttons} /> } : {}),
    }),
    [props.countdown, props.buttons],
  );

  const svenssonsArgs: { [placeholder: string]: React.ReactNode } = React.useMemo(
    () => ({
      ...(props.countdown ? { countdown: <Countdown countdown={props.countdown} /> } : undefined),
    }),
    [props.countdown],
  );

  const content = (
    <Document
      styleDecorators={isSvenssons ? svenssonsStyles : styles}
      decorators={decorators}
      elements={props.content}
      args={isSvenssons ? svenssonsArgs : args}
    />
  );
  const hero = (
    <>
      <Media image={image} />
      <Content css={contentDecorator}>
        {props.useMaxWidth ? <Wrapper>{content}</Wrapper> : content}
        {isSvenssons && props.buttons && (
          <ButtonsWrapper>
            {props.buttons.map((button, index) => (
              <ButtonBlock key={button.id ?? index} {...button} />
            ))}
          </ButtonsWrapper>
        )}
      </Content>
    </>
  );

  return (
    <Base
      id={props.id}
      css={styled(
        contentWidthDecorator(props.contentWidth),
        props.marginBottom && marginDecorator,
        props.contentWidth === 'normal' && desktopEdgeMarginDecorator,
      )}
      ref={self}
    >
      {props.link ? (
        <Link
          {...linkWithoutQueryParams(props.link)}
          onClick={() => {
            addSelectPromotionToDataLayer({ content: props });
            ninetailedClickEvent();
          }}
        >
          {hero}
        </Link>
      ) : (
        hero
      )}
    </Base>
  );
}

const marginDecorator = styled(
  createMaxSmallViewportDecorator({
    marginBottom: t => t.spacing.m,
  }),
  {
    marginBottom: t => t.sharedSpacing.xxl,
  },
);

const styles: StyleDecorators = {
  h1: styled(createMinLargeViewportDecorator({ fontSize: t => t.fontSize.xxxl }), {
    fontSize: t => t.fontSize.xl,
    fontFamily: t => t.fontFamily?.secondary,
    textAlign: 'center',
  }),
  h2: styled(createMinLargeViewportDecorator({ fontSize: t => t.fontSize.xl }), {
    fontSize: t => t.fontSize.l,
    fontFamily: t => t.fontFamily?.secondary,
    textAlign: 'center',
  }),
  h3: styled({
    fontSize: t => t.fontSize.l,
    textAlign: 'center',
  }),
  p: styled(createMinLargeViewportDecorator({ marginBottom: t => t.spacing.l }), {
    marginBottom: t => t.spacing.m,
    textAlign: 'center',
    lineHeight: 'normal',
  }),
};

const svenssonsStyles: StyleDecorators = {
  h1: styled(
    createMinLargeViewportDecorator({
      marginBottom: t => t.spacing.xs,
    }),
    {
      lineHeight: 'normal',
      marginBottom: t => t.spacing.xxs,
    },
  ),
  h2: styled(
    createMinLargeViewportDecorator({
      marginBottom: t => t.spacing.xs,
    }),
    {
      lineHeight: 'normal',
      marginBottom: t => t.spacing.xs,
    },
  ),
  h3: styled(
    createMinLargeViewportDecorator({
      marginBottom: t => t.spacing.xs,
    }),
    {
      lineHeight: 'normal',
      marginBottom: t => t.spacing.xs,
    },
  ),
  p: styled(createMinLargeViewportDecorator({ marginBottom: t => t.spacing.xs }), {
    lineHeight: 'normal',
    marginBottom: t => t.spacing.xxs,
  }),
};

const Base = styled(Block, {
  position: 'relative',
  overflow: 'hidden',
});

const Media = styled(Image, {
  width: '100%',
});

const Content = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  color: 'white',
  padding: { x: t => t.spacing.m },
});

const placementDecorator = styled(
  createMinLargeViewportDecorator({
    left: t => t.sharedSpacing.xxl,
    right: t => t.sharedSpacing.xxl,
  }),
);

const Wrapper = styled.div({
  maxWidth: 800,
});

const ButtonsWrapper = styled.div({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  columnGap: t => t.spacing.s,
});
